import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

interface SlotPopoverProps {
  anchor: { x: number; y: number; width: number; height: number } | null;
  cursor?: { x: number; y: number } | null;
  title: string;
  subtitle?: string;
  participants: string[];
  currentUser?: string | null;
  isMobile: boolean;
  onClose: () => void;
  onToggle?: () => void;
  toggleLabel?: string;
}

export function SlotPopover({
  anchor,
  cursor,
  title,
  subtitle,
  participants,
  currentUser,
  isMobile,
  onClose,
  onToggle,
  toggleLabel,
}: SlotPopoverProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (isMobile || !anchor || !ref.current) {
      setPos(null);
      return;
    }
    const el = ref.current;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const offset = 14;

    let top: number;
    let left: number;

    if (cursor) {
      left = cursor.x + offset;
      top = cursor.y + offset;
      if (left + rect.width > window.innerWidth - margin) {
        left = cursor.x - rect.width - offset;
      }
      if (top + rect.height > window.innerHeight - margin) {
        top = cursor.y - rect.height - offset;
      }
    } else {
      top = anchor.y - rect.height - margin;
      if (top < margin) top = anchor.y + anchor.height + margin;
      left = anchor.x + anchor.width / 2 - rect.width / 2;
    }

    left = Math.max(margin, Math.min(window.innerWidth - rect.width - margin, left));
    top = Math.max(margin, Math.min(window.innerHeight - rect.height - margin, top));
    setPos({ top, left });
  }, [anchor, cursor, isMobile]);

  useEffect(() => {
    const onDoc = (e: MouseEvent | TouchEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!anchor) return null;

  const body = (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-semibold text-room-text">{title}</div>
        {subtitle ? <div className="text-xs text-room-text-dim">{subtitle}</div> : null}
      </div>
      {participants.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {participants.map(p => {
            const isMe = currentUser && p === currentUser;
            return (
              <span
                key={p}
                className={`rounded-full px-2 py-0.5 text-[11px] ${
                  isMe
                    ? 'bg-room-accent text-[#06231D]'
                    : 'bg-room-surface-2 text-room-text-dim'
                }`}
              >
                {p}
              </span>
            );
          })}
        </div>
      ) : (
        <div className="text-xs italic text-room-text-faint">{t('calendar.slot.noOneYet')}</div>
      )}
      {onToggle ? (
        <button
          type="button"
          onClick={() => {
            onToggle();
            onClose();
          }}
          className="mt-1 w-full rounded-md bg-room-accent px-3 py-2 text-sm font-medium text-[#06231D] hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent"
        >
          {toggleLabel ?? t('calendar.slot.toggleAvailability')}
        </button>
      ) : null}
    </div>
  );

  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/30" onClick={onClose} />
        <div
          ref={ref}
          className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-room-border bg-room-surface p-4 pb-6 shadow-2xl"
          style={{ animation: 'slotpop-up 180ms ease-out' }}
        >
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-room-border-strong" />
          {body}
        </div>
        <style>{`@keyframes slotpop-up { from { transform: translateY(100%);} to { transform: translateY(0);} }`}</style>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      ref={ref}
      className="pointer-events-none fixed z-50 w-[240px] rounded-lg border border-room-border bg-room-surface p-3 shadow-xl"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, opacity: pos ? 1 : 0 }}
    >
      {body}
    </div>,
    document.body
  );
}
